import React, { useEffect, useState } from "react";
import { StepConfig } from "../../contexts";
import { PencilWaveIcon } from "../../assets/icons";

type Props = {
  currentStep: [number, number];
  steps: StepConfig;
};

const ProgressBar = ({ steps, currentStep }: Props) => {
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const listener = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsMobile(
        (e as MediaQueryList).matches ?? (e as MediaQueryListEvent).matches
      );
    // Initial sync
    listener(mq);
    mq.addEventListener("change", listener as EventListener);
    return () => mq.removeEventListener("change", listener as EventListener);
  }, []);

  const visibleSteps = React.useMemo(
    () => steps.filter((s) => !s.hideProgress),
    [steps]
  );
  const currentVisibleIndex = React.useMemo(() => {
    const currentName = steps[currentStep[0]]?.name;
    return visibleSteps.findIndex((s) => s.name === currentName);
  }, [steps, visibleSteps, currentStep]);

  return (
    <div className="cx-w-full cx-bg-white-80">
      {isMobile && currentVisibleIndex > -1 && (
        <div className="cx-flex cx-justify-between cx-items-center cx-px-4 cx-pt-4">
          <div className="cx-flex cx-items-center cx-gap-2">
            <PencilWaveIcon className="cx-w-5 cx-h-5" />
            <span className="cx-text-base cx-font-semibold">
              Application in process
            </span>
          </div>
          <span className="cx-text-sm cx-font-semibold cx-text-brand-primary-regular">
            Step {currentVisibleIndex + 1} of {visibleSteps.length}
          </span>
        </div>
      )}
      <div className="cx-flex cx-flex-row cx-justify-center cx-w-full cx-px-2 cx-py-4">
        <>
          {steps
            .slice(
              isMobile ? currentStep[0] : 0,
              isMobile ? currentStep[0] + 2 : steps.length
            )
            .map((step) => {
              let status = MilestoneStatusType.CURRENT;
              if (currentStep[0] > steps.findIndex((s) => s.name === step.name))
                status = MilestoneStatusType.COMPLETED;
              if (currentStep[0] < steps.findIndex((s) => s.name === step.name))
                status = MilestoneStatusType.INCOMPLETE;
              return !step.hideProgress ? (
                <Milestone
                  key={step.name}
                  title={step.name}
                  numSubSteps={step.subSteps.length}
                  currentSubStep={currentStep[1]}
                  status={status}
                />
              ) : null;
            })}
        </>
      </div>
    </div>
  );
};

enum MilestoneStatusType {
  COMPLETED = "completed",
  CURRENT = "current",
  INCOMPLETE = "incomplete",
}

type MilestoneProps = {
  title: string;
  status: MilestoneStatusType;
  numSubSteps?: number;
  currentSubStep?: number;
};

const Milestone = ({
  title,
  numSubSteps,
  currentSubStep,
  status,
}: MilestoneProps) => {
  return (
    <div className="cx-flex-col cx-justify-start cx-items-center cx-m-2 cx-w-47 cx-flex">
      <div className="cx-flex cx-flex-row cx-w-full cx-items-center">
        {status === "current" && numSubSteps && numSubSteps > 1 ? (
          <>
            <div className="cx-grow-0 cx-w-1 cx-h-1 cx-bg-brand-primary-regular cx-rounded-checkbox cx-mx-0.5"></div>
            {Array.from({ length: currentSubStep! }, (_, idx) => {
              return (
                <React.Fragment key={idx}>
                  <div className="cx-grow cx-h-0.5 cx-bg-brand-primary-regular cx-rounded-checkbox"></div>
                  <div className="cx-grow-0 cx-w-1 cx-h-1 cx-bg-brand-primary-regular cx-rounded-checkbox cx-mx-0.5"></div>
                </React.Fragment>
              );
            })}
            {Array.from(
              { length: numSubSteps! - currentSubStep! - 1 },
              (_, idx) => {
                return (
                  <React.Fragment key={idx}>
                    <div className="cx-grow cx-h-0.5 cx-bg-background-tertiary cx-rounded-checkbox"></div>
                    <div className="cx-grow-0 cx-w-1 cx-h-1 cx-bg-background-tertiary cx-rounded-checkbox cx-mx-0.5"></div>
                  </React.Fragment>
                );
              }
            )}
          </>
        ) : (
          <>
            {status === "completed" ||
            (numSubSteps === 1 && status === "current") ? (
              <div className="cx-grow cx-h-0.5 cx-bg-brand-primary-regular cx-rounded-checkbox"></div>
            ) : (
              <div className="cx-grow cx-h-0.5 cx-bg-background-tertiary cx-rounded-checkbox"></div>
            )}
          </>
        )}
      </div>
      <div
        className={`cx-text-xs cx-mt-2 cx-text-center cx-uppercase cx-tracking-[1px] ${
          status === "incomplete"
            ? "cx-text-text-tertiary"
            : "cx-text-brand-primary-regular cx-font-semibold"
        }`}
      >
        {title}
      </div>
    </div>
  );
};

export default ProgressBar;
